import {ButtonComponent} from "../../buttons/button/button.component";
import {ButtonConnotation, ButtonLayout, ButtonProminence} from "../../buttons/button/button";
import {IconButtonComponent} from "../../buttons/icon-button/icon-button.component";
import {IconButtonType} from "../../buttons/icon-button/icon-button";
import {LinkComponent} from "../../buttons/link/link.component";
import {DecimalFieldComponent} from '../../inputs/decimal-field/decimal-field.component';
import {FieldPairComponent} from '../../inputs/form/field-pair/field-pair.component';
import {FormComponent} from '../../inputs/form/form.component';
import {PhoneNumberFieldComponent} from '../../inputs/phone-number-field/phone-number-field.component';
import {SelectorControlComponent} from '../../inputs/selector-control/selector-control.component';
import {TextAreaFieldComponent} from '../../inputs/text-area-field/text-area-field.component';
import {TextFieldComponent} from '../../inputs/text-field/text-field.component';
import {
    IntegratedDeliveryAddressValidationRequestData,
    IntegratedDeliveryAddressValidationService
} from "../integrated-delivery-address-validation.service";
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    effect,
    inject,
    OnInit,
    signal,
    Signal,
    viewChild,
    WritableSignal
} from "@angular/core";
import {EntityData, provideSearchModel} from "@fedelta-pos/data-client";
import {SessionModel} from "../../session/session.model";
import {ResponsiveDirective} from "../../responsive/responsive.directive";
import {ResponsiveService} from "../../responsive/responsive.service";
import {ButtonShape, ButtonStyle, TableLookupMethod} from "../../configuration/app-entity.model";
import {navParam} from "../../navigation/navigation-params";
import {Page, PageGeometry} from "../../navigation/page";
import {LoadingService} from "../../ui-elements/loading/loading.service";
import {
    LocationSearchCoordinates,
    LocationSearchPanelComponent
} from "../location-search-panel/location-search-panel.component";
import {DeliveryDetails, OrderingContext, OrderingContextIntention, TableSelection} from '../ordering-context';
import {ContentMessageComponent} from "../../notification/message/content-message.component";
import {MessageDetails, MessageLevel} from "../../notification/message/message";
import {NotificationService} from "../../notification/notification.service";
import {ChewzieTableLookupService, TableLookupResponse} from "../chewzie-table-lookup.service";
import {ConfigurationModel, ParamConfig} from "../../configuration/configuration.model";
import {AppDeviceService} from '../../app-device/app-device.service';
import {IdentityModel} from '../../identity/identity.model';
import {OrderingContextModel, OrderingContextStoreSearchFilterProvider} from "../ordering-context.model";
import {isNumber} from '../../utils/string-utils';
import {BeaconData, BeaconOrderType, BeaconSearchModel} from '../../beacon/beacon-search.model';
import {StoreDetailsPanelComponent} from '../../store/store-details-panel/store-details-panel.component';
import {StoreData, StoreSearchModel, TableData} from '../../store/store-search.model';
import {AddressData} from '../../customer-info/address.data';
import {OrderType} from '../../order/order';
import {NgClass} from '@angular/common';
import {TranslateModule} from '@ngx-translate/core';
import {ModalPageLayoutComponent} from '../../page-layout/modal-page-layout/modal-page-layout.component';
import {OrderTypeSelectionPanelComponent} from "../order-type-selection-panel/order-type-selection-panel.component";
import {FormsModule} from '@angular/forms';
import {PhoneNumberDirective} from '../../inputs/phone-number-field/phone-number.directive';
import {ZXingScannerComponent, ZXingScannerModule} from '@zxing/ngx-scanner';
import {throttleTime} from 'rxjs';
import {Result} from '@zxing/library';
import {GroupByPipe} from '../../utils/group-by.pipe';
import {PageFooterControlDirective} from '../../page-layout/page-footer-control.directive';
import {onEach} from '../../utils/observable-utils';
import {LoadingStatus} from '../../session/loading-status';
import {SpinnerComponent} from '../../ui-elements/spinner/spinner.component';
import {ActivatableDirective} from '../../ui-elements/activatable.directive';
import {ExternalApiBridgeService, GeocodeResult, LatLng} from '../../external-api/external-api-bridge.service';

@Component({
    selector: "woa-ordering-context-selection-page",
    templateUrl: "./ordering-context-selection-page.component.html",
    styleUrl: "./ordering-context-selection-page.component.scss",
    providers: [
        provideSearchModel(StoreSearchModel),
        ChewzieTableLookupService,
        ExternalApiBridgeService,
        {provide: Page, useExisting: OrderingContextSelectionPageComponent}
    ],
    imports: [
        ButtonComponent,
        ContentMessageComponent,
        DecimalFieldComponent,
        FieldPairComponent,
        FormComponent,
        FormsModule,
        GroupByPipe,
        IconButtonComponent,
        LinkComponent,
        LocationSearchPanelComponent,
        ModalPageLayoutComponent,
        PageFooterControlDirective,
        NgClass,
        OrderTypeSelectionPanelComponent,
        PhoneNumberDirective,
        PhoneNumberFieldComponent,
        ResponsiveDirective,
        SelectorControlComponent,
        StoreDetailsPanelComponent,
        TextAreaFieldComponent,
        TextFieldComponent,
        TranslateModule,
        ZXingScannerModule,
        SpinnerComponent,
        ActivatableDirective
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class OrderingContextSelectionPageComponent extends Page implements OnInit {
    private destroyRef: DestroyRef = inject(DestroyRef);
    private notificationService: NotificationService = inject(NotificationService);
    private loadingService: LoadingService = inject(LoadingService);
    private integratedDeliveryAddressValidationService: IntegratedDeliveryAddressValidationService = inject(IntegratedDeliveryAddressValidationService);
    private tableLookupService: ChewzieTableLookupService = inject(ChewzieTableLookupService);
    private beaconSearchModel: BeaconSearchModel = inject(BeaconSearchModel);
    private appDeviceService: AppDeviceService = inject(AppDeviceService);
    private externalApiBridgeService: ExternalApiBridgeService = inject(ExternalApiBridgeService);
    public identityModel: IdentityModel = inject(IdentityModel);
    public configurationModel: ConfigurationModel = inject(ConfigurationModel);
    public sessionModel: SessionModel = inject(SessionModel);
    public orderingContextModel: OrderingContextModel = inject(OrderingContextModel);
    public responsiveService: ResponsiveService = inject(ResponsiveService);
    public storeSearchModel: StoreSearchModel = inject(StoreSearchModel);

    static readonly deliveryEntrySelectionGeometry: PageGeometry = {
        height: "650px"
    };
    static readonly desktopOrderTypeSelectionGeometry: PageGeometry = {
        height: "520px"
    };
    static readonly desktopStoreSelectionGeometry: PageGeometry = {
        pillarboxedOnDesktop: "none",
        width: "850px",
        height: "750px"
    };
    private static readonly desktopTableScannerGeometry: PageGeometry = {
        pillarboxedOnDesktop: "none",
        width: "600px",
        height: "600px"
    };
    private static readonly mobileTableScannerGeometry: PageGeometry = {
        height: "450px"
    };

    static readonly stateNames: string[][] = [
        ["act", "australian capital territory"],
        ["nsw", "new south wales"],
        ["nt", "northern territory"],
        ["qld", "queensland"],
        ["sa", "south australia"],
        ["tas", "tasmania"],
        ["vic", "victoria"],
        ["wa", "western australia"]
    ];

    LoadingStatus = LoadingStatus;
    OrderType = OrderType;
    ButtonProminence = ButtonProminence;
    ButtonConnotation = ButtonConnotation;
    TableLookupMethod = TableLookupMethod;
    MessageLevel = MessageLevel;
    IconButtonType = IconButtonType;
    ButtonShape = ButtonShape;
    ButtonStyle = ButtonStyle;
    ButtonLayout = ButtonLayout;

    public readonly form: Signal<FormComponent | undefined> = viewChild(FormComponent);
    private readonly scanner: Signal<ZXingScannerComponent | undefined> = viewChild("scanner");

    public readonly orderingContext: WritableSignal<OrderingContext> = navParam.required<OrderingContext>("orderingContext");

    public readonly loadingStatus: WritableSignal<LoadingStatus> = signal(undefined);

    public readonly orderType: WritableSignal<OrderType> = signal(
        this.orderingContext().selectedOrderType === OrderType.NONE ? undefined : this.orderingContext().selectedOrderType);
    public readonly canOrderAtTable: WritableSignal<boolean> = signal(false);
    public readonly canOrderAhead: WritableSignal<boolean> = signal(false);
    public readonly canOrderDelivery: WritableSignal<boolean> = signal(false);

    public readonly showAllStores: WritableSignal<boolean> = signal(false);
    public readonly position: WritableSignal<GeolocationPosition> = signal(undefined);
    public readonly location: WritableSignal<LocationSearchCoordinates> = signal(undefined);
    private readonly orderAheadStoreOption: WritableSignal<StoreData> = signal(undefined);
    private readonly orderDeliveryStoreOption: WritableSignal<StoreData> = signal(undefined);
    private readonly orderAtTableStoreOption: WritableSignal<StoreData> = signal(undefined);
    private readonly storeOptionsChecked: WritableSignal<boolean> = signal(false);
    private readonly canFilterByState: WritableSignal<boolean> = signal(undefined);
    private readonly storeOrderTypeSelectionRequired: WritableSignal<boolean> = signal(undefined);
    private readonly orderTypeSelectionRequired: WritableSignal<boolean> = signal(undefined);

    public readonly scannerAvailable: Signal<boolean> = computed(() => !!navigator.mediaDevices?.getUserMedia);
    public readonly scannerMsg: WritableSignal<string> = signal(this.scannerAvailable() ? undefined : "woa.error.ordering-context.scanUnavailable");
    public readonly scanning: WritableSignal<boolean> = signal(false);
    public readonly showCodeScanned: WritableSignal<boolean> = signal(false);
    public readonly manualTableCode: WritableSignal<string> = signal(undefined);
    public readonly filteredTableOptions: WritableSignal<TableData[]> = signal([]);
    public readonly selectingTable: WritableSignal<boolean> = signal(false);
    // If a table number relates to a single store (restaurant):
    public readonly selectedStoreForTable: WritableSignal<StoreData> = signal(undefined);
    // If a table number relates to multiple stores (food court) (and to carry other flags provided by table lookup service):
    public readonly selectedTable: WritableSignal<TableSelection> = signal(undefined);
    public readonly tableLookupCode: WritableSignal<string> = signal(undefined);
    public readonly tableLookupCodeMsg: WritableSignal<string> = signal(undefined);
    public readonly tableSelectionMethod: WritableSignal<TableLookupMethod> = signal(this.sessionModel.effectiveTableLookupMethod());

    private readonly treatDeliveryAddressAsUnconfirmed: WritableSignal<boolean> = signal(false);
    private readonly editingDeliveryAddress: WritableSignal<boolean> = signal(false);
    private readonly addressError: WritableSignal<string> = signal(undefined);
    public readonly addressErrorStore: WritableSignal<StoreData> = signal(undefined);

    public readonly street: WritableSignal<string> = signal(undefined);
    public readonly city: WritableSignal<string> = signal(undefined);
    public readonly state: WritableSignal<string> = signal(undefined);
    public readonly postCode: WritableSignal<string> = signal(undefined);
    public readonly deliveryInstructions: WritableSignal<string> = signal(undefined);
    public readonly orderMobilePhone: WritableSignal<string> = signal(undefined);
    public readonly saveForMember: WritableSignal<boolean> = signal(undefined);
    public readonly hasExistingAddress: WritableSignal<boolean> = signal(undefined);
    public readonly addressMatchesMember: WritableSignal<boolean> = signal(undefined);
    private readonly saveForMemberSpecified: WritableSignal<boolean> = signal(false);

    public readonly title: Signal<string> = computed(() =>
        this.loadingStatus() === LoadingStatus.LOADING ? ""
            : this.showTableSelection() ? (
                    this.configurationModel.isWaiterActor() ? "woa.heading.ordering-context.selectTable.waiter"
                        : (this.tableSelectionMethod() === TableLookupMethod.MANUAL ? "woa.heading.ordering-context.selectTable.query"
                            : this.orderingContext().selectedStore ? "woa.heading.ordering-context.scanTable.withStore"
                                : "woa.heading.ordering-context.scanTable.noStore"))
                : this.showOrderTypeSelection() ? (this.orderingContext().intention === OrderingContextIntention.VIEW_STORES ? "woa.heading.ordering-context.orderType.stores" : "woa.heading.ordering-context.orderType.query")
                    : this.showDeliveryAddressEntry && this.orderingContext().intention === OrderingContextIntention.CHANGE_FULFILMENT_DETAILS ? "woa.heading.edit-delivery-address.title"
                        : this.orderingContext().intention === OrderingContextIntention.VIEW_STORES ? (
                                this.orderType() === OrderType.ORDER_AHEAD ? "woa.heading.ordering-context.find-store.orderAhead"
                                    : this.orderType() === OrderType.ORDER_DELIVERY ? "woa.heading.ordering-context.find-store.delivery"
                                        : this.orderType() === OrderType.ORDER_AT_TABLE ? "woa.heading.ordering-context.find-store.orderAtTable"
                                            : "woa.heading.ordering-context.findStore")
                            : this.configurationModel.isWaiterActor() ? "woa.heading.ordering-context.select-store.waiter"
                                : this.orderType() === OrderType.ORDER_AHEAD ? "woa.heading.ordering-context.select-store.orderAhead"
                                    : this.orderType() === OrderType.ORDER_DELIVERY ? "woa.heading.ordering-context.select-store.delivery"
                                        : this.orderType() === OrderType.ORDER_AT_TABLE ? "woa.heading.ordering-context.select-store.orderAtTable"
                                            : "woa.heading.ordering-context.selectStore.title");

    public readonly customGeometry: Signal<PageGeometry> = computed(() =>
        this.showDeliveryAddressEntry() && this.orderingContext().intention === OrderingContextIntention.CHANGE_FULFILMENT_DETAILS ? OrderingContextSelectionPageComponent.deliveryEntrySelectionGeometry
            : this.showTableSelection() ? (
                    this.tableSelectionMethod() === TableLookupMethod.MANUAL ? undefined
                        : this.responsiveService.mobile() ? OrderingContextSelectionPageComponent.mobileTableScannerGeometry
                            : OrderingContextSelectionPageComponent.desktopTableScannerGeometry
                )
                : this.responsiveService.mobile() ? undefined
                    : this.storeOptionsChecked() && !this.showOrderTypeSelection() ? (this.configurationModel.isWaiterActor() ? undefined : OrderingContextSelectionPageComponent.desktopStoreSelectionGeometry)
                        : OrderingContextSelectionPageComponent.desktopOrderTypeSelectionGeometry);

    public readonly isViewIntention: Signal<boolean> = computed(() =>
        this.orderingContext().intention === OrderingContextIntention.VIEW_STORES ||
        this.orderingContext().intention === OrderingContextIntention.VIEW_CURRENT_STORE);

    public readonly noStoresInLocationMessage: Signal<string> = computed(() =>
        this.orderType() === OrderType.ORDER_AHEAD ? "woa.message.ordering-context.noOrderAheadStoresInLocation"
            : this.orderType() === OrderType.ORDER_DELIVERY ? "woa.message.ordering-context.noOrderDeliveryStoresInLocation"
                : this.orderType() === OrderType.ORDER_AT_TABLE ? "woa.message.ordering-context.noOrderAtTableStoresInLocation"
                    : "woa.message.ordering-context.noStoresInLocation");

    public readonly hasConfirmedDeliveryAddress: Signal<boolean> = computed(() =>
        !this.treatDeliveryAddressAsUnconfirmed() && (!!this.orderingContext().selectedDeliveryDetails || this.orderingContextModel.addressTrusted()));

    public readonly showDeliveryAddressInHeader: Signal<boolean> = computed(() =>
        this.orderType() === OrderType.ORDER_DELIVERY && !this.editingDeliveryAddress() && this.hasConfirmedDeliveryAddress());

    public readonly deliveryAddressError: Signal<string> = computed(() =>
        this.orderType() === OrderType.ORDER_DELIVERY && this.orderingContext().selectedDeliveryDetails ? this.addressError() : undefined);

    public readonly showDeliveryAddressEntry: Signal<boolean> = computed(() =>
        this.orderType() === OrderType.ORDER_DELIVERY && !this.deliveryAddressError() &&
        (this.editingDeliveryAddress() || this.orderingContext().intention === OrderingContextIntention.CHANGE_FULFILMENT_DETAILS || (!this.isViewIntention() && !this.hasConfirmedDeliveryAddress())));

    public readonly showAddressSearchInHeader: Signal<boolean> = computed(() =>
        (this.orderingContext().intention === OrderingContextIntention.VIEW_STORES && !this.showStoreOrderTypeOptions()) ||
        this.orderType() === OrderType.ORDER_AHEAD ||
        this.orderType() === OrderType.ORDER_AT_TABLE ||
        (this.orderType() === OrderType.ORDER_DELIVERY && !this.showDeliveryAddressInHeader() && !this.showDeliveryAddressEntry()));

    public readonly formattedDeliveryAddress: Signal<string> = computed(() =>
        this.orderingContext().selectedDeliveryDetails
            ? this.orderingContext().selectedDeliveryDetails.address.formattedAddress
            : this.orderingContextModel.address().formattedAddress);

    public readonly showStoreOrderTypeOptions: Signal<boolean> = computed(() =>
        !!this.orderType() && this.storeOrderTypeSelectionRequired());

    public readonly showOrderTypeSelection: Signal<boolean> = computed(() =>
        this.orderingContext().intention === OrderingContextIntention.SELECT_ORDER_TYPE || (!this.orderType() && this.orderTypeSelectionRequired()));

    // Indicates that the user should select from a list of configured tables when manual table selection is required instead of a manual entry textbox
    public readonly useTableListForManualSelection: Signal<boolean> = computed(() => this.configurationModel.isWaiterActor());

    public readonly showTableSelection: Signal<boolean> = computed(() =>
        !this.showOrderTypeSelection() && this.selectingTable());

    public readonly showTableOptions: Signal<boolean> = computed(() =>
        this.showTableSelection() && this.useTableListForManualSelection() && this.tableSelectionMethod() === TableLookupMethod.MANUAL);

    public readonly showStoreSelection: Signal<boolean> = computed(() =>
        !this.showOrderTypeSelection() && !this.showTableSelection());

    public readonly showStoreHeader: Signal<boolean> = computed(() =>
        this.configurationModel.isConsumerActor() && this.showStoreSelection() && (this.showAddressSearchInHeader() || this.showDeliveryAddressInHeader() || !!this.deliveryAddressError() || this.showStoreOrderTypeOptions()));

    public readonly showNearestStores: Signal<boolean> = computed(() =>
        !this.orderingContextModel.singleStoreLocation() && !this.showAllStores() && !this.showDeliveryAddressEntry());

    public readonly filteredStores: Signal<(stores: StoreData[]) => StoreData[]> = computed(() => {
        const orderType: OrderType = this.orderType();
        const orderingContext: OrderingContext = this.orderingContext();
        const location: LocationSearchCoordinates = this.location();
        const canFilterByState: boolean = this.canFilterByState();
        return this.showDeliveryAddressInHeader()
            ? () => this.orderingContextModel.deliveryStores()
            : (stores: StoreData[]) => stores.filter((store: StoreData) =>
                !(orderType && !store.canOrder(orderType)) &&
                ((orderingContext.selectedTable && orderingContext.selectedTable.storeIds && orderingContext.selectedTable.storeIds.length)
                    ? !!orderingContext.selectedTable.storeIds.find((storeId: number) => storeId === store.storeId)
                    : true) &&
                !(canFilterByState && location && location.stateOnly && this.stateName(store.state) !== this.stateName(location.stateOnly)));
    });

    public readonly showStateWithName: Signal<boolean> = computed(() =>
        this.showAllStores() && (!this.location() || !!this.location().countryOnly));

    public readonly currentStore: Signal<StoreData> = computed(() =>
        this.orderingContext().selectedStore ?? this.orderingContextModel.currentStore());

    public readonly tables: Signal<TableData[]> = computed(() =>
        this.orderingContext().selectedStore ? this.orderingContext().selectedStore.availableTables : this.orderingContextModel.tables());

    public readonly tableLookupRequired: Signal<boolean> = computed(() =>
        !!this.tables().length);

    public readonly allowAlphaTableNumbers: Signal<boolean> = computed(() =>
        this.tables().some((tcb: TableData) => !isNumber(tcb.tableLookupCode)));

    public readonly showMessageTableOrderingNotAvailableNearestStore: Signal<boolean> = computed(() =>
        this.canOrderAtTable() && (!this.storeOrderTypeSelectionRequired() || this.orderType() === OrderType.ORDER_AT_TABLE) &&
        this.storeSearchModel.nearestStores().length && !this.storeSearchModel.nearestStores()[0].orderAtTableStore);

    public readonly nearestStoreName: Signal<string> = computed(() =>
        this.storeSearchModel.nearestStores().length ? this.storeSearchModel.nearestStores()[0].collectiveStoreName : undefined);

    public readonly collapseStoreText: Signal<boolean> = computed(() =>
        this.responsiveService.mobile() && (!this.configurationModel.app()?.showStoreImagesOnMobileStoreSelection || this.configurationModel.app()?.showStoreAddrOnStoreSelection));

    public readonly showStoreImage: Signal<boolean> = computed(() =>
        this.responsiveService.mobile() ? this.configurationModel.app()?.showStoreImagesOnMobileStoreSelection : this.configurationModel.app()?.showStoreImagesOnStoreSelection);

    public readonly deliveryAddressConfirmLabel: Signal<string> = computed(() =>
        !this.hasExistingAddress() || this.hasConfirmedDeliveryAddress() ? "woa.button.common.continue" : "woa.button.ordering-context.confirmDeliveryAddress");

    /**
     * Checks for any stores of the same name at the same location with different ordering types.
     * We do not want to show these in the same list as the user should not perceive them as separate stores.
     */
    private readonly storePersonalitiesUsed: Signal<boolean> = computed<boolean>(() =>
        this.storeSearchModel.dataSignal()?.some((store: StoreData) => this.storeSearchModel.dataSignal()?.some((otherStore: StoreData) =>
            store !== otherStore && store.collectiveStoreName === otherStore.collectiveStoreName && store.locationId === otherStore.locationId &&
            (store.orderAheadStore !== otherStore.orderAheadStore || store.orderDeliveryStore !== otherStore.orderDeliveryStore || store.orderAtTableStore !== otherStore.orderAtTableStore))));

    constructor() {
        super();

        if (this.configurationModel.isWaiterActor()) {
            this.orderingContext.set(this.orderingContext()
                .withSelectedTable(undefined)
                .withSelectedOrderType(OrderType.ORDER_AT_TABLE)
                .withSelectedStore(this.orderingContextModel.currentStore()));
        }

        effect(() => {
            const scannerComponent: ZXingScannerComponent | undefined = this.scanner();
            if (scannerComponent) {
                onEach(scannerComponent.scanSuccess.pipe(throttleTime(2000)), (scannedText: string) => this.scanSuccess(scannedText), this.destroyRef);
            }
        });
    }

    ngOnInit(): void {
        void (async () => this.position.set(await this.orderingContextModel.getCurrentLocation(5000)))();
        this.load();
    }

    public load(): void {
        this.loadingStatus.set(LoadingStatus.LOADING);

        if (this.appDeviceService.deviceLocationId()) {
            this.storeSearchModel.setFilterValue(StoreSearchModel.FILTER_LOCATION_ID, this.appDeviceService.deviceLocationId());
        }
        if (this.orderingContext().selectedTable && (this.orderingContext().selectedTable.storeIds || this.orderingContext().selectedTable.storeGroup)) {
            this.storeSearchModel.setFilterValue(OrderingContextStoreSearchFilterProvider.FILTER_IGNORE_EXCLUSIVE, true);
            if (this.orderingContext().selectedTable.storeIds) {
                this.storeSearchModel.setFilterValue(StoreSearchModel.FILTER_STORES, this.orderingContext().selectedTable.storeIds);
            } else {
                this.storeSearchModel.setFilterValue(StoreSearchModel.FILTER_STORE_GROUP, this.orderingContext().selectedTable.storeGroup);
            }
        }
        if (this.orderingContext().selectedOrderType === OrderType.ORDER_AHEAD) {
            this.storeSearchModel.setFilterValue(StoreSearchModel.FILTER_ORDER_AHEAD_ONLY, 1);
        } else if (this.orderingContext().selectedOrderType === OrderType.ORDER_DELIVERY) {
            this.storeSearchModel.setFilterValue(StoreSearchModel.FILTER_ORDER_DELIVERY_ONLY, 1);
        } else if (this.orderingContext().selectedOrderType === OrderType.ORDER_AT_TABLE) {
            this.storeSearchModel.setFilterValue(StoreSearchModel.FILTER_ORDER_AT_TABLE_ONLY, 1);
        }

        void this.loadingService.performWithSpinner(async () => {
            try {
                await this.storeSearchModel.retrieve();
                this.checkStoreOptions();
                this.orderTypeChanged();

                if (this.configurationModel.isWaiterActor()) {
                    this.filterTableOptions();
                }

            } catch (error) {
                console.log("Store load failed:", error);
                this.loadingService.dismissSpinner();
                this.loadingStatus.set(LoadingStatus.FAILED);
                this.notificationService.logErrorToServer("OrderingContextSelectionPageComponent.load - store load failed", undefined, error);
            }
            if (!this.identityModel.isGuestUser() && !this.identityModel.currentMember()) {
                await this.identityModel.loadMemberSummary();
            }
        }, 20000, undefined, undefined, false);

        this.initialiseAddress();
        this.clearStoreAndTable();
    }

    private initialiseAddress(): void {
        if (this.orderingContextModel.address()) {
            this.street.set(this.orderingContextModel.address().street);
            this.city.set(this.orderingContextModel.address().city);
            this.state.set(this.orderingContextModel.address().state);
            this.postCode.set(this.orderingContextModel.address().postCode);
            this.deliveryInstructions.set(this.orderingContextModel.deliveryInstructions());
            this.orderMobilePhone.set(this.orderingContextModel.mobilePhone());
            if (this.street() || this.city()) {
                this.hasExistingAddress.set(true);
            }
        } else {
            this.street.set(undefined);
            this.city.set(undefined);
            this.state.set(undefined);
            this.postCode.set(undefined);
            this.deliveryInstructions.set(undefined);
            this.orderMobilePhone.set(undefined);
            this.hasExistingAddress.set(false);
        }
        this.deliveryDetailsChanged();
    }

    /**
     * Analyses the available stores to determine which options are available to the user
     */
    private checkStoreOptions(): void {
        if (!this.orderingContext().selectedMenuItem && this.orderingContextModel.currentStoreLocation()) {
            this.orderAheadStoreOption.set(this.orderingContextModel.currentStoreLocation().getStoreForOrderType(OrderType.ORDER_AHEAD));
            this.orderDeliveryStoreOption.set(this.orderingContextModel.currentStoreLocation().getStoreForOrderType(OrderType.ORDER_DELIVERY));
            this.orderAtTableStoreOption.set(this.orderingContextModel.currentStoreLocation().getStoreForOrderType(OrderType.ORDER_AT_TABLE));
        }

        this.canOrderAhead.set((!this.orderingContext().selectedOrderType || this.orderingContext().selectedOrderType === OrderType.ORDER_AHEAD) &&
            (!!this.orderAheadStoreOption() || this.storeSearchModel.hasOrderAheadStores()));
        this.canOrderDelivery.set((!this.orderingContext().selectedOrderType || this.orderingContext().selectedOrderType === OrderType.ORDER_DELIVERY) &&
            (!!this.orderDeliveryStoreOption() || this.storeSearchModel.hasOrderDeliveryStores()));
        this.canOrderAtTable.set((!this.orderingContext().selectedOrderType || this.orderingContext().selectedOrderType === OrderType.ORDER_AT_TABLE) &&
            (!!this.orderAtTableStoreOption() || this.storeSearchModel.hasOrderAtTableStores()));

        this.canFilterByState.set(this.orderingContextModel.hasAnyStoresWithState(this.storeSearchModel.dataSignal()));

        // Determine the combination of ordering options available
        let orderOptions: number = 0;
        if (this.canOrderAhead()) {
            orderOptions++;
        }
        if (this.canOrderDelivery()) {
            orderOptions++;
        }
        if (this.canOrderAtTable()) {
            orderOptions++;
        }

        let defaultToDelivery: boolean = false;

        // Determine whether an order type selection is required, or if each store panel handles the choice between available order types.
        // The intention is to only show the selector when necessary.

        // Only order type selection required?
        if (this.orderingContext().intention === OrderingContextIntention.SELECT_ORDER_TYPE) {
            this.orderTypeSelectionRequired.set(true);
        }

        // If there is only one option, then no selection is required.
        else if (orderOptions <= 1) {
            this.storeOrderTypeSelectionRequired.set(false);
            this.orderTypeSelectionRequired.set(false);
        }

        // If delivery is an option, the choice of stores may be different based on the confirmed delivery address, or in order mode, address entry may be required.
        else if (this.canOrderDelivery()) {
            if (!this.isViewIntention()) {
                this.storeOrderTypeSelectionRequired.set(true);
                this.orderTypeSelectionRequired.set(true);
            } else if (this.hasConfirmedDeliveryAddress()) {
                this.storeOrderTypeSelectionRequired.set(true);
                this.orderTypeSelectionRequired.set(false);

                // Need a default selection - can't show the order type selector with nothing selected.
                defaultToDelivery = true;
            } else {
                this.storeOrderTypeSelectionRequired.set(this.storePersonalitiesUsed());
                this.orderTypeSelectionRequired.set(this.storeOrderTypeSelectionRequired());
            }
        }

        // Otherwise, if all stores do not support all available ordering types, a selection is required.
        else {
            this.storeOrderTypeSelectionRequired.set(this.storePersonalitiesUsed());
            this.orderTypeSelectionRequired.set(this.storeOrderTypeSelectionRequired());
        }

        if (this.orderingContext().selectedOrderType === OrderType.ORDER_DELIVERY) {
            if (this.orderingContext().intention === OrderingContextIntention.CHANGE_FULFILMENT_DETAILS ||
                (!this.isViewIntention() && this.hasConfirmedDeliveryAddress())) {
                this.treatDeliveryAddressAsUnconfirmed.set(true);
            }
            if (this.orderingContext().intention === OrderingContextIntention.CHANGE_FULFILMENT_DETAILS) {
                this.storeOrderTypeSelectionRequired.set(false);
                this.orderTypeSelectionRequired.set(false);
            }
        } else if (this.orderingContext().selectedOrderType === OrderType.ORDER_AT_TABLE) {

            // Changing table selection
            if (this.orderingContext().intention === OrderingContextIntention.CHANGE_FULFILMENT_DETAILS) {
                this.selectingTable.set(true);
            }

            // Store has already been selected
            else if (this.orderingContext().selectedStore) {
                this.selectingTable.set(true);
            }

            // Changing the order type (and no preselected table)
            else if (this.orderingContextModel.orderType() !== OrderType.ORDER_AT_TABLE && !this.orderingContextModel.hasPreselectedTable()) {
                this.selectingTable.set(true);
            }

            // Neither a current store nor a preselected table exist in the session model (initial table ordering choice)
            else if (!this.orderingContextModel.currentStore() && !this.orderingContextModel.hasPreselectedTable()) {
                this.selectingTable.set(true);
            }

            if (this.selectingTable()) {
                this.orderTypeSelectionRequired.set(false);
            }
        }

        // If a default order type is set on the ordering context, use it if order type selection is required.
        // Otherwise, default the order type if there is only one option.
        // Otherwise, if selection is required, default it only if already set in the session model.
        if (this.orderingContext().defaultOrderType) {
            if (this.storeOrderTypeSelectionRequired()) {
                this.orderType.set(this.orderingContext().defaultOrderType);
            }
        } else if (!this.orderingContext().selectedOrderType) {
            this.orderType.set(undefined);
            if (orderOptions == 1) {
                if (this.orderingContextModel.orderType()) {
                    this.orderType.set(this.orderingContextModel.orderType());
                } else if (this.canOrderAhead()) {
                    this.orderType.set(OrderType.ORDER_AHEAD);
                } else if (this.canOrderDelivery()) {
                    this.orderType.set(OrderType.ORDER_DELIVERY);
                } else if (this.canOrderAtTable()) {
                    this.orderType.set(OrderType.ORDER_AT_TABLE);
                }
            } else if (this.storeOrderTypeSelectionRequired()) {
                this.orderType.set(this.orderingContextModel.orderType());

                if (!this.orderType() && defaultToDelivery) {
                    this.orderType.set(OrderType.ORDER_DELIVERY);
                }
            }
        }

        this.storeOptionsChecked.set(true);
    }

    public async orderTypeSelected(orderType: OrderType): Promise<void> {
        if (this.orderingContext().intention === OrderingContextIntention.SELECT_ORDER_TYPE && orderType === this.orderingContextModel.orderType()) {
            void this.dismiss(this.orderingContext());
            return;
        }

        let store: StoreData;
        if (this.orderingContext().intention !== OrderingContextIntention.SELECT_STORE) {
            store = orderType === OrderType.ORDER_AHEAD ? this.orderAheadStoreOption()
                : orderType === OrderType.ORDER_DELIVERY ? this.orderDeliveryStoreOption()
                    : orderType === OrderType.ORDER_AT_TABLE ? this.orderAtTableStoreOption()
                        : undefined;
        }
        this.orderingContext.set(this.orderingContext()
            .withSelectedStore(store)
            .withSelectedOrderType(orderType));

        // Nothing more to do for order type selection only or for pickups if the store is known
        if (this.orderingContext().intention === OrderingContextIntention.SELECT_ORDER_TYPE || (orderType === OrderType.ORDER_AHEAD && store)) {
            void this.dismiss(this.orderingContext());
            return;
        }

        // Store is selected by table code for table ordering rather than explicit selection
        if (orderType === OrderType.ORDER_AT_TABLE && (this.orderingContext().intention === OrderingContextIntention.ORDER || this.orderingContext().intention === OrderingContextIntention.SELECT_STORE)) {
            return this.startSelectFulfilmentDetails(this.orderingContext()
                .withSelectedOrderType(OrderType.ORDER_AT_TABLE));
        }

        this.orderType.set(orderType);
        this.orderTypeChanged();
    }

    public changeOrderType(orderType: OrderType): void {
        this.orderType.set(orderType);
        this.orderTypeChanged();
    }

    private orderTypeChanged(): void {
        if (this.orderingContext().intention !== OrderingContextIntention.VIEW_STORES) {
            if (this.orderType() === OrderType.ORDER_DELIVERY) {
                this.location.set(undefined);
            } else if (this.editingDeliveryAddress()) {
                this.editingDeliveryAddress.set(false);
            }
        }
        void this.findNearestStores();
    }

    async locationSelected(location: LocationSearchCoordinates): Promise<void> {
        this.location.set(location);
        this.loadingStatus.set(LoadingStatus.LOADING);
        await this.loadingService.performWithSpinner(async () => await this.findNearestStores());
    }

    editDeliveryAddress(): void {
        if (this.orderingContext().selectedDeliveryDetails) {
            this.addressError.set(undefined);
        }
        this.editingDeliveryAddress.set(true);
    }

    public async clearDeliveryAddress(): Promise<void> {
        this.orderingContext.set(this.orderingContext().withSelectedDeliveryDetails(new DeliveryDetails()));
        this.orderingContextModel.deliveryStores.set(null);
        this.initialiseAddress();
        this.form()?.markAsUnsubmitted();
        this.checkStoreOptions();
        await this.loadingService.performWithSpinner(async () => await this.findNearestStores());
    }

    public async confirmDeliveryAddress(): Promise<void> {
        if (!this.form()?.valid) {
            return;
        }

        try {
            const geocodeResult: GeocodeResult = await this.externalApiBridgeService.geocode({
                address: this.street() + ", " + this.city() + ", " + this.state() + (this.postCode() ? (" " + this.postCode()) : "")
            });
            if (!geocodeResult) {
                return this.notificationService.displayAlert("woa.heading.address.invalidAddress", "woa.message.address.invalidAddress");
            }
            const location: LatLng = geocodeResult.location;
            console.log("Address lookup result:", geocodeResult, location.lat, location.lng);
            if (geocodeResult.addressComponentTypes.find((types: string[]) => types.find((type: string) => type == "locality") != undefined) == undefined) {
                return this.notificationService.displayAlert("woa.heading.address.invalidAddress", "woa.message.address.invalidAddress");
            }

            this.editingDeliveryAddress.set(this.orderingContext().intention === OrderingContextIntention.CHANGE_FULFILMENT_DETAILS);
            this.treatDeliveryAddressAsUnconfirmed.set(this.orderingContext().intention === OrderingContextIntention.CHANGE_FULFILMENT_DETAILS);
            this.loadingStatus.set(this.orderingContext().intention === OrderingContextIntention.CHANGE_FULFILMENT_DETAILS ? LoadingStatus.LOADED : LoadingStatus.LOADING);

            this.orderingContext.set(this.orderingContext().withSelectedDeliveryDetails(new DeliveryDetails(
                this.street(), this.city(), this.state(), this.postCode(), this.orderMobilePhone(),
                this.deliveryInstructions(), this.saveForMemberSpecified() && this.saveForMember(), location.lat, location.lng)));
            this.orderingContextModel.deliveryStores.set(null);

            if (!await this.loadingService.performWithSpinner(async () => await this.findNearestStores())) {
                this.editingDeliveryAddress.set(false);
                this.treatDeliveryAddressAsUnconfirmed.set(false);
                return;
            }

            // If a delivery store has already been pre-selected, we can immediately proceed with ordering using that store
            if (this.orderingContext().selectedStore && (this.orderingContext().intention === OrderingContextIntention.ORDER || this.orderingContext().intention === OrderingContextIntention.SELECT_ORDER_TYPE)) {
                this.loadingService.dismissSpinner();
                void this.selectContext(this.orderingContext().selectedStore, OrderType.ORDER_DELIVERY);
            } else if (this.orderingContext().intention === OrderingContextIntention.CHANGE_FULFILMENT_DETAILS) {
                this.orderingContextModel.applyDeliveryDetails(this.orderingContext().selectedDeliveryDetails);
                void this.dismiss();
            }
        } catch (e) {
            console.error("Geocoding failed", e);
            void this.notificationService.displayAlert("woa.heading.address.invalidAddress", "woa.message.address.invalidAddress");
        }
    }

    public deliveryDetailsChanged(): void {
        if (this.identityModel.isGuestUser()) {
            return;
        }
        if (!this.identityModel.currentIdentity().id || !this.identityModel.currentIdentity().homeAddress) {
            this.addressMatchesMember.set(false);
        } else {
            this.addressMatchesMember.set(
                this.identityModel.currentIdentity().homeAddress.street === this.street() &&
                this.identityModel.currentIdentity().homeAddress.city === this.city() &&
                this.identityModel.currentIdentity().homeAddress.state === this.state() &&
                this.identityModel.currentIdentity().homeAddress.postCode === this.postCode() &&
                this.identityModel.currentIdentity().mobilePhone === this.orderMobilePhone() &&
                this.identityModel.currentIdentity().deliveryInstructions === this.deliveryInstructions());
        }
        if (this.addressMatchesMember()) {
            // reset - address is "implicitly saved"
            this.saveForMember.set(true);
            this.saveForMemberSpecified.set(false);
        } else if (!this.saveForMemberSpecified()) {
            // they need to opt in to saving it when they change from the default
            this.saveForMember.set(this.orderingContextModel.saveAddressForMember());
            if (this.saveForMember()) {
                this.saveForMemberSpecified.set(true);
            }
        }
    }

    public saveDeliveryAddressForMemberChanged(): void {
        this.saveForMemberSpecified.set(true);
    }

    /**
     * Reloads the nearest stores data based on the order type.
     * @returns false if this is a delivery order and the confirmed address either has no valid delivery store, or is invalid for the current store (if any); true otherwise
     */
    private async findNearestStores(): Promise<boolean> {
        this.showAllStores.set(false);

        try {
            if (this.orderType() !== OrderType.ORDER_DELIVERY) {

                // If a geographic location has been specified, base the nearest stores on that location.
                if (this.location()) {

                    // If the location only specifies a state, then the lat/long values are simply the geographic centre of the state.
                    // Since this is not particularly useful, we instead show all stores in the specified state, ignoring store range and nearest store limits.
                    if (this.location().stateOnly || this.location().countryOnly) {
                        this.showAllStores.set(true);
                        this.storeSearchModel.clearNearestStores();
                    } else {
                        this.storeSearchModel.findNearestStores(
                            this.configurationModel.app()?.storeLimit, this.configurationModel.app()?.storeSearchRange, this.configurationModel.app()?.deliveryStoreRange,
                            this.location().lat, this.location().lng);
                    }
                }

                // For waiters, we show all stores since they have already been filtered by the registered location of the device
                else if (this.configurationModel.isWaiterActor()) {
                    this.showAllStores.set(true);
                    this.storeSearchModel.clearNearestStores();
                }

                // If no geographic location has been specified, attempt to use the device's current location. If this fails, then we will show all stores grouped by state.
                else {
                    try {
                        if (!this.position()) {
                            this.position.set(await this.orderingContextModel.getCurrentLocation(5000));
                        }
                        if (this.position()) {
                            this.storeSearchModel.findNearestStores(
                                this.configurationModel.app()?.storeLimit, this.configurationModel.app()?.storeSearchRange, this.configurationModel.app()?.deliveryStoreRange,
                                this.position().coords.latitude, this.position().coords.longitude);
                        } else {
                            this.showAllStores.set(true);
                            this.storeSearchModel.clearNearestStores();
                        }
                    } catch (error) {
                        this.showAllStores.set(true);
                        this.storeSearchModel.clearNearestStores();
                    }
                }
            } else if (this.orderingContext().selectedDeliveryDetails) {
                this.addressError.set(undefined);

                // Delivery stores are always based on the delivery address, and are cached in the session model to avoid the
                // repeated overhead and cost of a distance by road search.
                if (!this.orderingContextModel.deliveryStores()) {
                    this.addressError.set(await this.orderingContextModel.findDeliveryStores(this.externalApiBridgeService, this.storeSearchModel, this.orderingContext().selectedDeliveryDetails));
                    if (this.addressError()) {
                        return false;
                    }
                }

                // Validate the current/selected store against the address
                const store: StoreData = this.orderingContext().selectedStore || this.orderingContextModel.currentStore();
                if (store) {
                    if (!this.orderingContextModel.deliveryStores().find((deliveryStore: StoreData) => deliveryStore.storeId === store.storeId)) {
                        this.addressError.set("woa.message.ordering-context.wrongStoreForDeliveryAddress");
                        return false;
                    }
                    if (store.integratedDelivery && !await this.validateIntegratedDeliveryAddress(store, this.orderingContext().selectedDeliveryDetails.address)) {
                        return false;
                    }
                }
            }

            return true;
        } finally {
            this.loadingService.dismissSpinner();
            this.loadingStatus.set(LoadingStatus.LOADED);
        }
    }

    private async validateIntegratedDeliveryAddress(store: StoreData, address: AddressData): Promise<boolean> {
        return this.loadingService.performWithSpinner(async () => {
            const integratedDeliveryAddressValidationRequest: IntegratedDeliveryAddressValidationRequestData = <IntegratedDeliveryAddressValidationRequestData>EntityData.create(IntegratedDeliveryAddressValidationRequestData);
            integratedDeliveryAddressValidationRequest.storeId = store.storeId;
            integratedDeliveryAddressValidationRequest.address = address;
            integratedDeliveryAddressValidationRequest.expectedCompletion = this.orderingContextModel.expectedCompletion();
            const result = await this.integratedDeliveryAddressValidationService.performAction(integratedDeliveryAddressValidationRequest);
            const valid: boolean = result["valid"];
            if (!valid) {
                this.addressError.set(this.orderingContextModel.singleStoreLocation() ? "woa.message.ordering-context.integratedDeliveryUnavailableSingleStore" : "woa.message.ordering-context.integratedDeliveryUnavailable");
                this.addressErrorStore.set(store);
            }
            return valid;
        }, 20000);
    }

    private stateName(alt: string): string {
        const stateNames: string[] = OrderingContextSelectionPageComponent.stateNames.find((alts: string[]) => alts.indexOf(alt.toLowerCase()) > -1);
        return stateNames ? stateNames[0] : undefined;
    }

    public async showStoreDetails(store: StoreData): Promise<void> {
        const orderingContext: OrderingContext = await this.navigationService.createPage("woa-store-details-page", {
            orderingContext: this.orderingContext()
                .withSelectedStore(store)
                .withSelectedOrderType(this.orderType())
        });
        if (orderingContext) {
            return this.selectContext(orderingContext.selectedStore, orderingContext.selectedOrderType);
        }
    }

    public async selectContext(store: StoreData, orderType: OrderType, table?: TableSelection): Promise<void> {
        // Note: if the ordering context has a selected table, the table number will be applied to the store
        // when the selection context is applied to the ordering context (see OrderingcontextModel.applyOrderingContext()).
        // That will also take care of resolving custom mappings for the table number against the selected store, or assigning the default naming.

        if (orderType === OrderType.ORDER_AT_TABLE && !this.orderingContext().selectedTable && !table) {
            return this.startSelectFulfilmentDetails(this.orderingContext()
                .withIntention(OrderingContextIntention.ORDER)
                .withSelectedStore(store)
                .withSelectedOrderType(orderType));
        }
        if (orderType === OrderType.ORDER_DELIVERY) {
            if (!this.hasConfirmedDeliveryAddress()) {
                return this.startSelectFulfilmentDetails(this.orderingContext()
                    .withIntention(OrderingContextIntention.ORDER)
                    .withSelectedStore(store)
                    .withSelectedOrderType(orderType));
            }
            if (store.integratedDelivery) {
                const address: AddressData = this.orderingContext().selectedDeliveryDetails ? this.orderingContext().selectedDeliveryDetails.address : this.orderingContextModel.address();
                if (!await this.validateIntegratedDeliveryAddress(store, address)) {
                    return;
                }
            }
        }
        void this.dismiss(this.orderingContext()
            .withSelectedStore(store)
            .withSelectedOrderType(orderType)
            .withSelectedTable(table || this.orderingContext().selectedTable));
    }

    private async startSelectFulfilmentDetails(orderingContext: OrderingContext): Promise<void> {
        this.orderingContext.set(orderingContext);
        this.orderType.set(orderingContext.selectedOrderType);
        if (orderingContext.selectedOrderType === OrderType.ORDER_AT_TABLE) {
            this.selectingTable.set(true);

            if (this.useTableListForManualSelection()) {
                this.filterTableOptions();
            }
        } else if (orderingContext.selectedOrderType === OrderType.ORDER_DELIVERY) {
            this.editingDeliveryAddress.set(true);
        }
    }

    public camerasFound(devices: MediaDeviceInfo[]): void {
        console.info("CAMERAS FOUND", devices);
        if (devices.length) {
            const video: HTMLVideoElement = this.scanner().previewElemRef?.nativeElement;
            video.addEventListener("playing", () => this.scanning.set(true));
        }
    }

    public camerasNotFound(error: any): void {
        console.log("CAMERAS NOT FOUND", error);
        this.tableSelectionMethod.set(TableLookupMethod.MANUAL);
    }

    public permissionResponse(permission: boolean): void {
        if (permission === false) {
            this.scannerMsg.set("woa.error.ordering-context.scanDenied");
        }
    }

    public scanError(error: Error): void {
        console.log("SCAN ERROR", error);
        this.scannerMsg.set("woa.error.ordering-context.scanFailed");
    }

    public scanComplete(result: Result): void {
        if (result) {
            console.info("SCAN COMPLETE", result);
        }
    }

    private async scanSuccess(scannedText: string): Promise<void> {
        console.log("SCANNED", scannedText);

        this.showCodeScanned.set(true);
        setTimeout(() => {
            this.showCodeScanned.set(false);
            this.lookupTable(scannedText);
        }, 500);
    }

    public tableFilterClearStore(): void {
        this.orderingContext.set(this.orderingContext().withSelectedStore(undefined));
        this.clearStoreAndTable();
        this.filterTableOptions();
    }

    public tableFilterSelectStore(): void {
        this.selectStoreAndTable(undefined, OrderType.ORDER_AT_TABLE, undefined);
    }

    /**
     * Filters the available table options based on the entered manualTableCode
     */
    public filterTableOptions(): void {
        this.tableLookupCodeMsg.set(undefined);
        if (!this.manualTableCode() || !this.manualTableCode().trim()) {
            // If no input, show all table numbers
            this.filteredTableOptions.set(this.tables().slice(0, 100));
        } else {
            const searchTerm = this.manualTableCode().toLowerCase().trim();

            // Filter table numbers that include the search term
            this.filteredTableOptions.set(this.tables()
                .filter((table) => table.tableLookupCode.toLowerCase().includes(searchTerm)));
        }

        if (this.filteredTableOptions().length === 0) {
            this.tableLookupCodeMsg.set("woa.error.ordering-context.noTablesFound");
        }
    }

    /**
     * Handles selection of a table from the available options
     * @param tableNumber The selected table number. If not specified, the query string and remaining filtered
     * values are checked for a likely match. This is a selection shortcut when enter is pressed.
     */
    public async selectTable(tableNumber?: string): Promise<void> {
        if (!tableNumber) {
            if (!tableNumber) {
                // Try exact match
                const foundTable: TableData = this.filteredTableOptions().find((table: TableData) => table.tableLookupCode === this.manualTableCode());
                if (foundTable) {
                    tableNumber = foundTable.tableLookupCode;
                }
            }

            if (this.filteredTableOptions().length === 1) {
                // 1 option left, select it
                tableNumber = this.filteredTableOptions()[0].tableLookupCode;
            }

            if (!tableNumber) {
                return;
            }
        }

        this.manualTableCode.set(tableNumber);
        await this.lookupTable(tableNumber, false);
    }

    async acceptTableCode(): Promise<void> {
        if (!this.form()?.valid) {
            return;
        }
        return this.lookupTable(this.manualTableCode(), this.configurationModel.app()?.onlineManualTableLookup);
    }

    private clearStoreAndTable(): void {
        this.selectedStoreForTable.set(undefined);
        this.selectedTable.set(undefined);
    }

    private selectStoreAndTable(store: StoreData, orderType: OrderType, table: TableSelection): void {
        this.selectedStoreForTable.set(store);
        this.orderType.set(orderType);
        this.selectedTable.set(table);
        if (store) {
            void this.selectContext(store, orderType, table);
        } else {
            this.selectingTable.set(false);
            this.orderingContext.set(this.orderingContext()
                .withSelectedOrderType(orderType)
                .withSelectedTable(table));
        }
    }

    async lookupTable(scannedText: string, useLookupService: boolean = true): Promise<void> {
        console.log("Scanned text: " + scannedText);
        console.log("Configured to use lookup service? " + useLookupService);

        this.clearStoreAndTable();
        this.tableLookupCodeMsg.set(undefined);

        await this.loadingService.performWithSpinner(async () => {

            // Analyse the scanned text
            this.tableLookupCode.set(undefined);
            let scannedCParam: string;
            let scannedGuid: string;
            let scannedTableLookupCode: string;
            let tableLookupResponse: TableLookupResponse;

            let scannedUrl: string = undefined;
            if (scannedText.startsWith("http") && scannedText.indexOf("?") >= 0) {
                const urlParams: URLSearchParams = new URLSearchParams(scannedText.substring(scannedText.indexOf("?")));
                scannedCParam = urlParams.get("c");
                scannedGuid = urlParams.get("id");
                scannedTableLookupCode = urlParams.get("tn");
                const tableLookupUrl: string = this.tableLookupService.tableLookupUrl();

                // If the scanned value matches the table lookup service URL, pass it directly to the lookup service
                if (scannedText.startsWith(tableLookupUrl)) {
                    scannedUrl = scannedText;
                }

                // Otherwise, if the scanned value is a URL with parameters that look like a table lookup, assume the URL has changed or been locally overridden, and rebuild it with the current URL
                else if (scannedCParam || scannedGuid) {

                    // C param is a base64-encoded json string potentially containing a beaconId
                    if (scannedCParam) {
                        const json = JSON.parse(atob(scannedCParam));
                        scannedGuid = json.beaconId;
                    }

                    if (scannedGuid) {
                        const url = new URL(tableLookupUrl + "/r");
                        url.searchParams.append("id", scannedGuid);
                        if (scannedTableLookupCode) {
                            url.searchParams.append("tn", scannedTableLookupCode);
                        }
                        scannedUrl = url.href;
                    }
                }

                // Otherwise, look for a lookupCode URL parameter for a local lookup (will default below to a local lookup of the whole scanned value)
                else {
                    this.tableLookupCode.set(urlParams.get("lookupCode"));
                }
            }

            // If the scanned value is not a URL, it may be a "canned" TableLookupResponse
            else if (scannedText.startsWith("rsp:")) {
                console.log(atob(scannedText.substring(4)));
                tableLookupResponse = Object.assign(new TableLookupResponse(), JSON.parse(atob(scannedText.substring(4))));
            }

            // Call Chewzie table lookup service or use canned response
            if (scannedUrl || tableLookupResponse) {
                if (useLookupService || tableLookupResponse) {
                    try {
                        if (!tableLookupResponse) {
                            try {
                                tableLookupResponse = await this.tableLookupService.lookupTable(scannedUrl);
                                console.log("Table lookup response", tableLookupResponse);
                            } catch (error) {
                                if (!scannedGuid) {
                                    throw error;
                                }
                                console.log("lookupTable: Table lookup service failed for scanned url: " + scannedUrl, error);
                                tableLookupResponse = new TableLookupResponse();
                            }
                        }

                        // If the scanned URL redirects outside this app, follow the redirect
                        const redirectURL: string = tableLookupResponse.redirectURL;
                        if (redirectURL) {
                            let redirectURLBase: string = redirectURL.split("?")[0];
                            if (redirectURLBase.endsWith("/")) {
                                redirectURLBase = redirectURLBase.substring(0, redirectURLBase.length - 1);
                            }
                            let myURLBase: string = window.location.href.split("?")[0];
                            if (myURLBase.endsWith("/")) {
                                myURLBase = myURLBase.substring(0, myURLBase.length - 1);
                            }
                            if (redirectURLBase !== myURLBase) {
                                if (!this.configurationModel.isWaiterActor()) {
                                    this.navigationService.redirectToUrl(redirectURL);
                                }
                                return;
                            }
                        }

                        // Local beacon lookup.
                        // If we found a beacon id, then do this even if the beacon was not registered with the lookup service.
                        if (!tableLookupResponse.id) {
                            tableLookupResponse.id = scannedGuid;
                        }
                        if (tableLookupResponse.id) {
                            const beacon: BeaconData = await this.beaconSearchModel.lookupBeacon(tableLookupResponse.id);
                            if (beacon) {
                                tableLookupResponse.exclusiveStores = beacon.appStore ? [beacon.appStore] : undefined;
                                tableLookupResponse.exclusiveStoreGroup = beacon.appStoreGroup;
                                tableLookupResponse.tableLookupCode = beacon.tableLookupCode;
                                tableLookupResponse.tableOnlyMode = beacon.orderType === BeaconOrderType.TABLE_ONLY || beacon.orderType === BeaconOrderType.TABLE_OR_PICKUP_ONLY;
                                tableLookupResponse.pickupOnlyMode = beacon.orderType === BeaconOrderType.PICKUP_ONLY || beacon.orderType === BeaconOrderType.TABLE_OR_PICKUP_ONLY;
                            } else {
                                const msg: string = "Scanned beacon id " + tableLookupResponse.id + " which is not registered for this app";
                                console.log("lookupTable: unknown beacon - " + msg);
                                this.tableLookupCodeMsg.set("woa.error.ordering-context.unrecognisedBeacon");
                                this.loadingStatus.set(LoadingStatus.LOADED);
                                return;
                            }
                        }

                        const storeGroup: string = tableLookupResponse.storeGroup || tableLookupResponse.exclusiveStoreGroup;
                        const storeIds: number[] = tableLookupResponse.stores || tableLookupResponse.exclusiveStores;

                        if (!storeGroup && (!storeIds || storeIds.length === 0)) {
                            console.log("lookupTable: no table registered for scanned url: " + scannedUrl);

                            // Fallback to custom table lookup using GUID as lookup code
                            scannedUrl = undefined;
                            tableLookupResponse = undefined;
                            this.tableLookupCode.set(scannedGuid ?? scannedTableLookupCode);
                        }

                        // "restaurant" case - single store for table
                        else if (storeIds && storeIds.length === 1) {
                            const storeId: number = storeIds[0];

                            // If a store has been selected (in the current ordering context), the store for the scanned table must match that store.
                            // In other words, if the user selects "order at table from here" for a store in the store list, they can't then scan a code for a different store.
                            if (this.orderingContext().selectedStore && this.orderingContext().selectedStore.storeId !== storeId) {
                                const msg: string = "Expecting store " + this.orderingContext().selectedStore.storeId + " but lookup code " + this.tableLookupCode() + " is for store " + storeId;
                                console.log("lookupTable: wrong store - " + msg);
                                this.tableLookupCodeMsg.set("woa.error.ordering-context.wrongStore");
                                this.loadingStatus.set(LoadingStatus.LOADED);
                                return;
                            }

                            if (tableLookupResponse.tableLookupCode) {

                                // Lookup custom table number
                                const lookupStore: StoreData = await this.orderingContextModel.lookupCustomTableNumber(tableLookupResponse.tableLookupCode, undefined, storeId);
                                if (lookupStore?.table.tableNumber) {
                                    this.selectStoreAndTable(lookupStore, OrderType.ORDER_AT_TABLE, {
                                        storeGroup: storeGroup,
                                        storeIds: storeIds,
                                        exclusiveStores: !!tableLookupResponse.exclusiveStoreGroup || !!tableLookupResponse.exclusiveStores,
                                        tableOnlyMode: tableLookupResponse.tableOnlyMode,
                                        pickupOnlyMode: tableLookupResponse.pickupOnlyMode
                                    });
                                } else {
                                    try {
                                        let store: StoreData = await this.findStore(storeId);
                                        if (store) {

                                            // Apply the default table prefix since there was no custom mapping found
                                            const tableStore: StoreData = <StoreData>EntityData.create(StoreData).assign(store);
                                            tableStore.table.tableNumber = await this.orderingContextModel.getDefaultTableNumber(tableLookupResponse.tableLookupCode);
                                            this.selectStoreAndTable(tableStore, OrderType.ORDER_AT_TABLE, {
                                                tableLookupCode: tableLookupResponse.tableLookupCode,
                                                tableNumber: tableStore.table.tableNumber,
                                                storeGroup: storeGroup,
                                                storeIds: storeIds,
                                                exclusiveStores: !!tableLookupResponse.exclusiveStoreGroup || !!tableLookupResponse.exclusiveStores,
                                                tableOnlyMode: tableLookupResponse.tableOnlyMode,
                                                pickupOnlyMode: tableLookupResponse.pickupOnlyMode
                                            });
                                        } else {
                                            console.log("lookupTable: no store for store id " + storeId);

                                            // Fallback to custom table lookup using GUID as lookup code
                                            scannedUrl = undefined;
                                            tableLookupResponse = undefined;
                                            this.tableLookupCode.set(scannedGuid ?? scannedTableLookupCode);
                                        }
                                    } catch (error) {
                                        this.loadingStatus.set(LoadingStatus.FAILED);
                                        return;
                                    }
                                }
                            }

                            // We have no table number, so revert to pickup
                            else {
                                try {
                                    let store: StoreData = await this.findStore(storeId);
                                    if (store) {
                                        const pickupStore: StoreData = <StoreData>EntityData.create(StoreData).assign(store);
                                        this.selectStoreAndTable(pickupStore, OrderType.ORDER_AHEAD, {
                                            exclusiveStores: !!tableLookupResponse.exclusiveStoreGroup || !!tableLookupResponse.exclusiveStores,
                                            storeGroup: tableLookupResponse.exclusiveStoreGroup,
                                            storeIds: tableLookupResponse.exclusiveStores,
                                            tableOnlyMode: tableLookupResponse.tableOnlyMode,
                                            pickupOnlyMode: tableLookupResponse.pickupOnlyMode
                                        });
                                    } else {
                                        console.log("lookupTable: no store");
                                        this.tableLookupCodeMsg.set("woa.error.ordering-context.noMatchingLookupCode");
                                    }
                                } catch (error) {
                                    this.loadingStatus.set(LoadingStatus.FAILED);
                                    return;
                                }
                            }
                        }

                        // "food court" case - store group or multiple stores with shared tables
                        else if (storeGroup || (storeIds && storeIds.length > 1)) {
                            if (!await this.selectPreselectedStoreWithTableCode(storeGroup, storeIds, tableLookupResponse.tableLookupCode, null,
                                !!tableLookupResponse.exclusiveStoreGroup || !!tableLookupResponse.exclusiveStores,
                                tableLookupResponse.tableOnlyMode, tableLookupResponse.pickupOnlyMode)) {

                                if (tableLookupResponse.tableLookupCode) {
                                    // Even though there are multiple stores, and any custom table number could theoretically be different for each one,
                                    // we assume for now that they are likely to be the same and that any mapping is better than none.
                                    // Once a store is selected, the custom lookup will be done again to make sure the mapping is correct.
                                    const lookupStore: StoreData = await this.orderingContextModel.lookupCustomTableNumber(tableLookupResponse.tableLookupCode, storeGroup, storeGroup ? undefined : storeIds[0],
                                        true, true);
                                    let tableNumber: string;
                                    if (lookupStore?.table.tableNumber) {
                                        console.log("lookupTable: candidate store found");
                                        tableNumber = lookupStore.table.tableNumber;
                                    } else {
                                        tableNumber = await this.orderingContextModel.getDefaultTableNumber(tableLookupResponse.tableLookupCode);
                                    }
                                    this.selectStoreAndTable(undefined, OrderType.ORDER_AT_TABLE, {
                                        tableLookupCode: tableLookupResponse.tableLookupCode,
                                        tableNumber: tableNumber,
                                        storeGroup: storeGroup,
                                        storeIds: storeIds,
                                        exclusiveStores: !!tableLookupResponse.exclusiveStoreGroup || !!tableLookupResponse.exclusiveStores,
                                        tableOnlyMode: tableLookupResponse.tableOnlyMode,
                                        pickupOnlyMode: tableLookupResponse.pickupOnlyMode
                                    });
                                }

                                // We have no table number, so revert to pickup
                                else {
                                    this.selectStoreAndTable(undefined, OrderType.ORDER_AHEAD, {
                                        storeGroup: storeGroup,
                                        storeIds: storeIds,
                                        exclusiveStores: !!tableLookupResponse.exclusiveStoreGroup || !!tableLookupResponse.exclusiveStores,
                                        tableOnlyMode: tableLookupResponse.tableOnlyMode,
                                        pickupOnlyMode: tableLookupResponse.pickupOnlyMode
                                    });
                                }
                            }
                        }
                    } catch (error) {
                        console.log("lookupTable: Table lookup service failed for scanned url: " + scannedUrl, error);

                        // Fallback to custom table lookup using GUID as lookup code
                        scannedUrl = undefined;
                        tableLookupResponse = undefined;
                        this.tableLookupCode.set(scannedGuid ?? scannedTableLookupCode);
                    }
                    this.loadingStatus.set(LoadingStatus.LOADED);
                } else {
                    scannedUrl = undefined;
                    tableLookupResponse = undefined;
                    this.tableLookupCode.set(scannedGuid ?? scannedTableLookupCode);
                }
            } else {
                console.log("Not using lookup service for this text");
            }

            // Manual selection from the table list
            if (!scannedUrl && !tableLookupResponse && this.useTableListForManualSelection()) {
                if (this.orderingContext().selectedStore) {
                    const tableStore: StoreData = <StoreData>EntityData.create(StoreData).assign(this.orderingContext().selectedStore);
                    await this.orderingContextModel.applyTableToStore(scannedText, tableStore);
                    this.selectStoreAndTable(tableStore, OrderType.ORDER_AT_TABLE, {
                        tableLookupCode: scannedText,
                        tableOnlyMode: true,
                        localTable: true
                    });
                } else {

                    // Find the store by the table number
                    const stores: StoreData[] = this.orderingContextModel.getStoresForTable(scannedText);
                    if (stores.length === 1) {
                        const tableStore: StoreData = <StoreData>EntityData.create(StoreData).assign(stores[0]);
                        await this.orderingContextModel.applyTableToStore(scannedText, tableStore);
                        this.selectStoreAndTable(tableStore, OrderType.ORDER_AT_TABLE, {
                            tableLookupCode: scannedText,
                            tableOnlyMode: true,
                            localTable: true
                        });
                    } else {
                        const storeIds: number[] = stores.map((store: StoreData) => store.storeId);
                        this.selectStoreAndTable(undefined, OrderType.ORDER_AT_TABLE, {
                            tableLookupCode: scannedText,
                            storeIds: storeIds,
                            tableOnlyMode: true,
                            localTable: true
                        });
                    }
                }
            }

            // Or do custom table lookup
            else if (!scannedUrl && !tableLookupResponse) {
                if (!this.tableLookupCode()) {
                    if (scannedText.startsWith("http")) {
                        this.tableLookupCodeMsg.set("woa.error.ordering-context.unrecognisedBeacon");
                        return;
                    }
                    this.tableLookupCode.set(scannedText);
                }

                const paramConfig: ParamConfig = await this.configurationModel.getParamConfig();

                // Table lookup is not required if there are no custom tables defined
                // (either for the current store if known, or otherwise for any store).
                if (this.tableLookupRequired()) {
                    this.storeSearchModel.setFilterValue(OrderingContextStoreSearchFilterProvider.FILTER_IGNORE_EXCLUSIVE, true);
                    this.storeSearchModel.setFilterValue(StoreSearchModel.FILTER_TABLE_LOOKUP_CODE, this.tableLookupCode());
                    this.storeSearchModel.setFilterValue(StoreSearchModel.FILTER_ORDER_LOCAL_DATE_TIME, this.orderingContextModel.expectedCompletionAsString());
                    try {
                        await this.storeSearchModel.retrieve();

                        this.loadingService.dismissSpinner();
                        this.loadingStatus.set(LoadingStatus.LOADED);

                        if (this.storeSearchModel.dataSignal()?.length === 0) {
                            console.log("lookupTable: no match");
                            this.tableLookupCodeMsg.set("woa.error.ordering-context.noMatchingLookupCode");
                        }

                        // "restaurant" case - single store for table
                        else if (this.storeSearchModel.dataSignal()?.length === 1) {
                            const store: StoreData = this.storeSearchModel.dataSignal()[0];

                            // If a store has been selected (in the current ordering context), the store for the table lookup code must match that store.
                            // In other words, if the user selects "order at table from here" for a store in the store list, they can't then scan a code for a different store.
                            if (this.orderingContext().selectedStore && this.orderingContext().selectedStore.storeId !== store.storeId) {
                                const msg: string = "Expecting " + this.orderingContext().selectedStore.storeName + " but lookup code " + this.tableLookupCode() + " is for " + store.storeName;
                                console.log("lookupTable: wrong store - " + msg);
                                this.tableLookupCodeMsg.set("woa.error.ordering-context.wrongStore");
                                return;
                            }

                            this.selectStoreAndTable(store, OrderType.ORDER_AT_TABLE, {
                                tableOnlyMode: true,
                                pickupOnlyMode: paramConfig.pickupOnlyMode || !paramConfig.tableOnlyMode,
                                deliveryOnlyMode: paramConfig.deliveryOnlyMode || (!paramConfig.tableOnlyMode && !paramConfig.pickupOnlyMode),
                                localTable: true
                            });
                        }

                        // "food court" case - multiple stores with shared tables
                        else {
                            const storeIds: number[] = this.storeSearchModel.dataSignal()?.map((store: StoreData) => store.storeId);
                            if (!await this.selectPreselectedStoreWithTableCode(undefined, storeIds, this.tableLookupCode(), this.storeSearchModel.dataSignal())) {

                                // Even though there are multiple stores, and any custom table number could theoretically be different for each one,
                                // we assume for now that they are likely to be the same and that any mapping is better than none.
                                // Once a store is selected, the custom lookup will be done again to make sure the mapping is correct.
                                this.selectStoreAndTable(undefined, OrderType.ORDER_AT_TABLE, {
                                    tableLookupCode: this.tableLookupCode(),
                                    tableNumber: this.storeSearchModel.dataSignal()[0].table.tableNumber,
                                    storeIds: storeIds,
                                    tableOnlyMode: true,
                                    pickupOnlyMode: paramConfig.pickupOnlyMode || !paramConfig.tableOnlyMode,
                                    deliveryOnlyMode: paramConfig.deliveryOnlyMode || (!paramConfig.tableOnlyMode && !paramConfig.pickupOnlyMode),
                                    localTable: true
                                });
                            }
                        }
                    } catch (error) {
                        console.log("lookupTable: store search error", error);
                        this.notificationService.displayFooterMessage(new MessageDetails(MessageLevel.ERROR, "woa.error.common.cannotCommunicateServer"));
                        this.loadingStatus.set(LoadingStatus.FAILED);
                    }
                } else if (scannedText.length > 5) {
                    this.tableLookupCodeMsg.set("woa.error.ordering-context.unrecognisedBeacon");
                    return;
                } else {
                    const store: StoreData = this.currentStore() ? <StoreData>EntityData.create(StoreData).assign(this.currentStore()) : undefined;
                    if (store) {
                        store.table = undefined;
                    }
                    this.selectStoreAndTable(store, OrderType.ORDER_AT_TABLE, {
                        tableLookupCode: this.tableLookupCode(),
                        tableNumber: this.tableLookupCode(),
                        tableOnlyMode: true,
                        pickupOnlyMode: paramConfig.pickupOnlyMode || !paramConfig.tableOnlyMode,
                        deliveryOnlyMode: paramConfig.deliveryOnlyMode || (!paramConfig.tableOnlyMode && !paramConfig.pickupOnlyMode),
                        localTable: true
                    });
                }
            }
        }, 8000);
    }

    /**
     * Try to find the specified store in the ones we already have loaded in the session model.
     * If it's not there, do a store search ignoring the exclusive filters.
     * @param storeId
     */
    private async findStore(storeId: number): Promise<StoreData> {
        let store: StoreData = this.orderingContextModel.stores().find((store: StoreData) => store.storeId === storeId);
        if (store) {
            return store;
        }
        this.storeSearchModel.setFilterValue(OrderingContextStoreSearchFilterProvider.FILTER_IGNORE_EXCLUSIVE, true);
        this.storeSearchModel.setFilterValue(StoreSearchModel.FILTER_STORES, [storeId]);
        this.storeSearchModel.setFilterValue(StoreSearchModel.FILTER_ORDER_LOCAL_DATE_TIME, this.orderingContextModel.expectedCompletionAsString());
        try {
            await this.storeSearchModel.retrieve();
            return this.storeSearchModel.dataSignal()?.[0];
        } catch (error) {
            console.log("lookupTable: store search error. storeId: " + storeId, error);
            this.notificationService.displayFooterMessage(new MessageDetails(MessageLevel.ERROR, "woa.error.common.cannotCommunicateServer"));
            throw error;
        }
    }

    private async selectPreselectedStoreWithTableCode(storeGroup: string, storeIds: number[], tableNumber: string, customTableStores?: StoreData[], exclusiveStores?: boolean, tableOnlyMode?: boolean, pickupOnlyMode?: boolean): Promise<boolean> {

        // If a store has been selected in the ordering context, the store group or one of the stores for the scanned code must match that store.
        let preselectedStore: StoreData = this.orderingContext().selectedStore;
        if (preselectedStore) {
            if (storeGroup) {
                if (preselectedStore.storeGroup !== storeGroup) {
                    const msg: string = "Expecting " + this.orderingContext().selectedStore.storeName + " but lookup code " + this.tableLookupCode() + " is for store group " + storeGroup;
                    console.log("lookupTable: wrong store - " + msg);
                    this.tableLookupCodeMsg.set("woa.error.ordering-context.wrongStore");
                    return false;
                }
            } else if (storeIds) {
                if (storeIds.indexOf(preselectedStore.storeId) === -1) {
                    const msg: string = "Expecting " + this.orderingContext().selectedStore.storeName + " but lookup code " + this.tableLookupCode() + " is for multiple other stores";
                    console.log("lookupTable: wrong store - " + msg);
                    this.tableLookupCodeMsg.set("woa.error.ordering-context.wrongStore");
                    return false;
                }
            }
        }

        // If a store is already present in the ordering context, we want to retain it if it matches the scanned code
        if (!preselectedStore && this.orderingContextModel.currentStore()) {
            if (storeGroup) {
                if (this.orderingContextModel.currentStore().storeGroup === storeGroup) {
                    preselectedStore = this.orderingContextModel.currentStore();
                }
            } else if (storeIds) {
                if (storeIds.indexOf(this.orderingContextModel.currentStore().storeId) > -1) {
                    preselectedStore = this.orderingContextModel.currentStore();
                }
            }
        }

        if (!preselectedStore) {
            return false;
        }

        if (tableNumber) {

            // If we already have the mappings from a custom table lookup, look for a custom mapping for the table number on the preselected store.
            //
            // Otherwise, we select a copy of the preselected store, but we don't set the table number on the store at this point.
            // Instead, we capture the selected table information in the ordering context.
            // When the selection context is applied to the ordering context (see OrderingcontextModel.applyOrderingContext()),
            // it will take care of resolving custom mappings for the table number against the selected store, or assigning the default naming.

            let store: StoreData;
            if (customTableStores) {
                store = customTableStores.find((store: StoreData) => store.storeId === preselectedStore.storeId);
            }
            if (!store) {
                store = <StoreData>EntityData.create(StoreData).assign(preselectedStore);
                store.table = undefined;
            }

            // We already know the store, but we want to retain the original scanned lookup code to allow remapping when swapping between stores sharing the table.
            this.selectStoreAndTable(store, OrderType.ORDER_AT_TABLE, {
                tableLookupCode: this.tableLookupCode() ?? tableNumber,
                tableNumber: tableNumber,
                storeGroup: storeGroup,
                storeIds: storeIds,
                exclusiveStores: exclusiveStores,
                tableOnlyMode: tableOnlyMode,
                pickupOnlyMode: pickupOnlyMode
            });
        }

        // We have no table number, so revert to pickup
        else {
            const store: StoreData = <StoreData>EntityData.create(StoreData).assign(preselectedStore);
            this.selectStoreAndTable(store, OrderType.ORDER_AHEAD, {
                storeGroup: storeGroup,
                storeIds: storeIds,
                exclusiveStores: exclusiveStores,
                tableOnlyMode: tableOnlyMode,
                pickupOnlyMode: pickupOnlyMode
            });
        }

        return true;
    }
}
